import React from 'react';
import { Tooltip, Typography } from '@mui/material';
import { useGlobalization } from '@Translations/useGlobalization';
import { coverageBand } from './coverageBand';

interface Props {
	// Grid rows carry only pct (DATA_COVERAGE). scored/total are optional, for
	// callers that have counts. Null/undefined when there is nothing to measure.
	coverage?: { pct: number; scored?: number; total?: number } | null;
	// Grid only: the run predates coverage capture, so say how to get it.
	showRerunHint?: boolean;
}

export const DataCoverageCell = ({
	coverage,
	showRerunHint = false,
}: Props) => {
	const { t } = useGlobalization();
	if (!coverage) {
		if (!showRerunHint) {
			return (
				<Typography variant="body2" sx={{ color: 'text.disabled' }}>
					—
				</Typography>
			);
		}
		const hint = t(
			'Coverage was not recorded for this run. Re-run to see it.',
		);
		return (
			<Tooltip arrow title={hint}>
				<Typography
					variant="body2"
					role="img"
					aria-label={hint}
					// eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- tooltip trigger: focusable so keyboard users can reach the reason
					tabIndex={0}
					sx={{ color: 'text.disabled' }}>
					—
				</Typography>
			</Tooltip>
		);
	}
	const { label, color } = coverageBand(coverage.pct, t);
	const { scored, total } = coverage;
	const tooltip =
		typeof scored === 'number' && typeof total === 'number'
			? t('{{scored}} of {{total}} values have a score.', {
					scored,
					total,
			  })
			: t('{{pct}}% of inputs came from recorded data', {
					pct: coverage.pct,
			  });
	return (
		<Tooltip arrow title={tooltip}>
			<Typography
				variant="body2"
				data-cy="data-coverage-cell"
				sx={{
					color,
					fontWeight: 600,
					textTransform: 'uppercase',
					fontSize: '0.75rem',
					letterSpacing: '0.03em',
				}}>
				{label}
			</Typography>
		</Tooltip>
	);
};

export default DataCoverageCell;
